import React, { useState, useEffect, useRef } from "react";
import { 
  Calendar, 
  Clock, 
  ArrowRight, 
  Check, 
  CheckCircle2, 
  User, 
  Star, 
  Menu, 
  X, 
  ChevronDown, 
  MapPin, 
  AlertCircle, 
  BookOpen, 
  Users, 
  Award, 
  Terminal, 
  Lock, 
  Sparkles, 
  Share2, 
  ChevronRight,
  Smartphone,
  CheckCircle,
  MessageSquare
} from "lucide-react";
import { Toaster, toast } from "./components/ui/sonner";
import "@/App.css";

// Links Provided by Creator
const WHATSAPP_COMMUNITY_LINK = "https://chat.whatsapp.com/LwYHigvQM0tCdewtHbqz9K";
const REGISTRATION_CHECKOUT_LINK = "https://learn.futureinnovatorsclub.com/web/checkout/6a40cfb3dbdc516b1c2a9a3a";
const SPEAKER_PHOTO = "https://tagmango.com/staticassets/-gemini_generated_image_dauhqudauhqudauh-5d15b858ac9b5aff6f167b8701473b80.png";
const HERO_SPEAKER_PHOTO = "https://tagmango.com/staticassets/-4_copy-a106b2dbd6474b54ee850bc41a0144a2.jpg";
const HEADER_LOGO = "https://tagmango.com/staticassets/-logo_rectangle-ae1ca23df3de531d89ef0214c531e1a2.png";

export default function App() {
  // Navigation & Redirection Popup Trigger States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isWhatsappPopupOpen, setIsWhatsappPopupOpen] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);

  // Form Lead-Capture States
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    situation: "",
    education: "",
    challenge: ""
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Scarcity Seats Left State
  const [seatsLeft, setSeatsLeft] = useState(14);
  const [showFloatingCta, setShowFloatingCta] = useState(false);

  // Active Agenda Category
  const [activeTab, setActiveTab] = useState("intro");
  const [openFaq, setOpenFaq] = useState(null);
  
  // Ref for checking scroll for Floating CTA
  const heroRef = useRef(null);

  // High-Performance Meta Ads Event Tracker Helper with Advanced Matching Optimization
  const trackEvent = (eventName, eventData = {}) => {
    console.log(`[Funnel Tracker] Event: ${eventName}`, eventData);
    if (typeof window !== "undefined" && window.fbq) {
      try {
        if (eventName === "form_submitted") {
          // Send Lead event with Advanced Matching details for maximum Match Quality Score on Meta Ads
          window.fbq("track", "Lead", {
            content_name: "AI Career Masterclass Form Lead",
            value: 0.00,
            currency: "INR",
            // Advanced Match Data (formatted for Meta Indexing)
            em: eventData.email ? eventData.email.toLowerCase().trim() : undefined,
            fn: eventData.fullName ? eventData.fullName.trim() : undefined,
            ph: eventData.phone ? eventData.phone.replace(/[^\d+]/g, "") : undefined,
            // Custom parameters
            situation: eventData.situation,
            education: eventData.education,
            challenge: eventData.challenge
          });
        } else if (eventName === "cta_clicked") {
          window.fbq("track", "InitiateCheckout", {
            content_name: "Reserve Seat CTA Click",
            content_category: "Webinar"
          });
        } else {
          window.fbq("trackCustom", eventName, eventData);
        }
      } catch (e) {
        console.error("Meta Pixel tracking error:", e);
      }
    }
  };

  // Check URL parameters for completed registration confirmation redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("success") === "true" || params.get("confirmed") === "true" || params.get("registration") === "done") {
      setIsConfirmed(true);
      setIsWhatsappPopupOpen(true);
      toast.success("Registration Confirmed! Spot Secured.");

      // Automatically redirect to the VIP WhatsApp group after 3 seconds
      const redirectTimer = setTimeout(() => {
        window.location.href = WHATSAPP_COMMUNITY_LINK;
      }, 3000);

      return () => clearTimeout(redirectTimer);
    }
  }, []);

  // Check scroll position to show/hide bottom floating CTA
  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const heroBottom = heroRef.current.getBoundingClientRect().bottom;
        if (heroBottom < 100) {
          setShowFloatingCta(true);
        } else {
          setShowFloatingCta(false);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Simulate real-time seats left decrementing
  useEffect(() => {
    const interval = setInterval(() => {
      setSeatsLeft((prev) => (prev > 3 ? prev - (Math.random() > 0.6 ? 1 : 0) : prev));
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  // Form Input Change Handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear validation error when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Form Validation Logic
  const validateForm = () => {
    let tempErrors = {};
    if (!formData.fullName.trim()) tempErrors.fullName = "Full Name is required";
    
    if (!formData.email.trim()) {
      tempErrors.email = "Email Address is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      tempErrors.email = "Enter a valid email address";
    }
    
    if (!formData.phone.trim()) {
      tempErrors.phone = "WhatsApp Number is required";
    } else if (!/^\+?[0-9\s-]{10,15}$/.test(formData.phone.replace(/[\s-]/g, ""))) {
      tempErrors.phone = "Enter a valid mobile/WhatsApp number";
    }
    
    if (!formData.situation) tempErrors.situation = "Please select what best describes you";
    if (!formData.education) tempErrors.education = "Please select your educational background";
    if (!formData.challenge) tempErrors.challenge = "Please select your biggest challenge";
    
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  // Redirect URL builder with UTM preservation and Pre-fill parameters
  const getRedirectUrl = (data) => {
    const searchParams = new URLSearchParams(window.location.search);
    
    // Set checkout field pre-fill parameters supported by TagMango
    searchParams.set("name", data.fullName || "");
    searchParams.set("email", data.email || "");
    searchParams.set("phone", data.phone || "");
    
    // Pass other qualification responses in query metadata
    searchParams.set("situation", data.situation || "");
    searchParams.set("education", data.education || "");
    searchParams.set("challenge", data.challenge || "");

    return `${REGISTRATION_CHECKOUT_LINK}?${searchParams.toString()}`;
  };

  // Form Submit Handler
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);
      trackEvent("form_submitted", {
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        situation: formData.situation,
        education: formData.education,
        challenge: formData.challenge
      });

      // Mimic highly fluid, fast-submitting save state before smooth redirect
      setTimeout(() => {
        setIsSubmitting(false);
        setIsModalOpen(false);
        
        const destinationUrl = getRedirectUrl(formData);
        trackEvent("redirect_to_registration", { destinationUrl });
        
        // Redirect directly in same tab to preserve checkout flow context
        window.location.href = destinationUrl;
      }, 1200);
    } else {
      toast.error("Please select and fill in all required fields.");
    }
  };

  // Unified CTA Clicks -> Opens lead popup modal
  const handleReserveClick = () => {
    setIsModalOpen(true);
    trackEvent("cta_clicked");
    trackEvent("popup_opened");
  };

  // FAQ Accordion Trigger Toggle
  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  // Interactive Tabs Data for Webinar Framework
  const agendaTabs = {
    intro: {
      title: "Clarity Before Coding",
      desc: "Understand your direction and identify the right AI career skills for your background. Cut through the noise and information overload.",
      bullets: [
        "How to choose the right AI career direction (Eng, Analysis, PM, etc.)",
        "The truth about coding: why you don't need a math or CS degree to build with AI",
        "Demystifying AI jargon: LLMs, Neural Networks, Agents explained simply"
      ]
    },
    demo: {
      title: "Portfolio Power",
      desc: "Learn how practical, hands-on projects create absolute proof of your skill level for recruiters, even if you are just starting out.",
      bullets: [
        "Live walk-through of building a practical AI-powered application from scratch",
        "How to host, launch and publish your AI app to get noticed by companies",
        "Structuring a 1-page high-impact AI portfolio that grabs recruiters' attention"
      ]
    },
    blueprint: {
      title: "The 90-Day Execution System",
      desc: "Understand how to approach your next 90 days with clear structure, daily habits, and consistent execution.",
      bullets: [
        "The Week-by-Week Roadmap: from absolute beginner to industry-ready project creator",
        "The 'Build in Public' strategy to gain massive organic career opportunities",
        "Balancing college, job search, or work-life while skill-building in just 1 hour a day"
      ]
    },
    qa: {
      title: "Live Q&A with Namita Desai",
      desc: "Bring your exact roadblocks and questions about your career, background, and aspirations to get a direct customized feedback.",
      bullets: [
        "Direct interaction and live advice from Namita Desai",
        "Resume and LinkedIn profile optimization tips for entry-level AI roles",
        "Exclusive free resources, AI cheat sheets, and FIC community invite codes"
      ]
    }
  };

  // Pain Points Data
  const painPoints = [
    {
      id: 1,
      quote: "I want to build a career in AI, but I don’t know where to start.",
      sub: "The path looks overwhelming with machine learning, python, deep learning, math..."
    },
    {
      id: 2,
      quote: "There is too much information, and I feel confused about what to learn.",
      sub: "Countless YouTube videos, courses, and certifications but zero structured path."
    },
    {
      id: 3,
      quote: "I don’t know which AI skills companies actually expect.",
      sub: "Feeling like college doesn't teach what recruiters actually hire for in 2026."
    },
    {
      id: 4,
      quote: "I have completed courses, but I still don’t have practical projects.",
      sub: "Generic certificate holders are everywhere. Real builders get the jobs."
    },
    {
      id: 5,
      quote: "I am afraid of coding and feel that AI is too difficult for me.",
      sub: "Believing you need to be a coding wizard when AI tools make development visual."
    },
    {
      id: 6,
      quote: "I am on a career break and don't know how to restart my career.",
      sub: "Wondering how to explain gaps and jump straight into high-paying modern tech roles."
    }
  ];

  // Learnings points
  const learnings = [
    {
      title: "The Right AI Career Direction",
      desc: "Understand exactly where you fit in the AI landscape based on your current background, strengths, and goals."
    },
    {
      title: "Essential Skills to Focus First",
      desc: "Skip the 2-year university math syllabus. Focus on high-value prompting, APIs, and modern orchestration tools."
    },
    {
      title: "Practical AI Projects Blueprint",
      desc: "Step-by-step instructions on building functional AI tools that solve real business problems, not generic tutorial copies."
    },
    {
      title: "Portfolio Proof Generation",
      desc: "How to package and present your work so that recruiters can interact with your apps directly instead of reading bullet points."
    },
    {
      title: "The 90-Day Step-by-Step Roadmap",
      desc: "A highly actionable week-by-week execution system designed to move you from absolute confusion to total industry readiness."
    },
    {
      title: "Consistent Confidence and Habits",
      desc: "Learn our unique systems to stay motivated, execute daily without burnout, and build in public for high visibility."
    }
  ];

  // Audience Segments
  const audienceSegments = [
    {
      title: "IT Students",
      desc: "College students wanting to skip outdated curricula and stand out in placements with bleeding-edge practical AI skills."
    },
    {
      title: "IT Freshers",
      desc: "Graduates struggling to land their first interview or job, needing actual industry-level projects to beat the fierce competition."
    },
    {
      title: "Career Restarters",
      desc: "People on a career break who want to bypass entry-level resume filters and restart strong in high-demand modern IT roles."
    },
    {
      title: "Absolute Beginners",
      desc: "Anyone feeling lost or confused by the AI hype, wanting a clear, friendly, and practical entry point into building with AI."
    }
  ];

  // FAQs Data
  const faqs = [
    {
      q: "Is this webinar really 100% free?",
      a: "Yes! This masterclass is entirely free. Our goal at Future Innovators Club is to bridge the gap between college education and industry-level practical AI skills. There are no hidden charges or forced payment walls."
    },
    {
      q: "Do I need prior coding experience to attend?",
      a: "No! This session is built with beginners and non-programmers in mind. We teach 'Clarity Before Coding'. You'll learn how to approach AI systems conceptually first, and how modern low-code/no-code AI tools enable you to build without complex programming."
    },
    {
      q: "Who is Namita Desai?",
      a: "Namita Desai is the Founder of Future Innovators Club, India's leading AI Mentor, and a Career Transformation Coach. She helps students and freshers move from passive AI learning to active project building, launching future-ready technical careers."
    },
    {
      q: "What is the format of the masterclass?",
      a: "The masterclass is an online, interactive online masterclass where you can interact directly and ask your questions live."
    },
    {
      q: "Will I get any certificate or resources?",
      a: "Yes! All live attendees will receive the complete 90-Day AI Career Blueprint, a curated list of top entry-level AI tools, and an exclusive certificate of participation to showcase on LinkedIn."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-inter selection:bg-[#FF5500] selection:text-white">
      <Toaster position="top-center" richColors theme="dark" />

      {/* TOP ANNOUNCEMENT BAR */}
      <div className="bg-[#FF5500] text-black font-semibold py-2 px-4 text-center text-xs md:text-sm tracking-wide font-outfit uppercase z-50 relative">
        <span className="inline-block animate-pulse mr-2">●</span> LIVE AI CAREER MASTERCLASS • ONLY {seatsLeft} FREE SEATS REMAINING TODAY!
      </div>

      {/* STICKY HEADER WITH LOGO IMAGE ONLY */}
      <header className="sticky top-0 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-zinc-800/80 py-4 px-4 md:px-8 z-40 transition-all duration-300">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center" data-testid="nav-logo">
            <img 
              src={HEADER_LOGO} 
              alt="Future Innovators Club Logo" 
              className="h-20 md:h-24 w-auto object-contain"
            />
          </div>
          
          <button 
            onClick={handleReserveClick}
            data-testid="nav-reserve-button"
            className="hidden sm:inline-flex items-center space-x-2 bg-[#FF5500] hover:bg-[#E04B00] text-white font-bold py-2.5 px-5 rounded-lg text-sm transition-all duration-300 transform active:scale-95 hover:shadow-lg hover:shadow-[#FF5500]/10"
          >
            <span>Reserve Free Spot</span>
            <ArrowRight className="w-4 h-4" />
          </button>
          
          <button 
            onClick={handleReserveClick}
            className="sm:hidden bg-[#FF5500] hover:bg-[#E04B00] text-white font-bold py-2 px-4 rounded-lg text-xs transition-all duration-300"
          >
            Reserve Spot
          </button>
        </div>
      </header>

      {/* HERO SECTION */}
      <section ref={heroRef} className="py-12 md:py-20 px-4 md:px-8 max-w-7xl mx-auto border-b border-zinc-900">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Hero Left Column */}
          <div className="lg:col-span-7 space-y-8">
            <div className="inline-flex items-center space-x-2 bg-zinc-900 border border-zinc-800 rounded-full py-1 px-3 md:py-1.5 md:px-4">
              <span className="flex h-2 w-2 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF5500] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF5500]"></span>
              </span>
              <span className="text-xs font-mono text-zinc-300 uppercase tracking-widest font-outfit">
                Interactive Live Masterclass for Students & Beginners
              </span>
            </div>

            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight text-white">
              How to Build a <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-white to-[#FF5500] inline">Future-Ready AI Career</span> in 90 Days
              <span className="block text-[#FF5500] mt-2 text-2xl sm:text-3xl lg:text-4xl font-semibold">
                — Even If You’re Starting From Scratch
              </span>
            </h1>

            <p className="text-zinc-300 text-lg md:text-xl leading-relaxed font-light max-w-xl">
              Stop feeling confused about what tools to learn and what projects to build. Learn the exact step-by-step framework to launch your AI journey.
            </p>

            {/* Evergreen Quick Benefits Feature Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-zinc-900/40 border border-zinc-800 p-4 rounded-xl">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 rounded bg-zinc-800 flex items-center justify-center text-[#FF5500] shrink-0 mt-0.5">
                  <Check className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-white">No Prior Coding Needed</h4>
                  <p className="text-xs text-zinc-400">Perfect entry point for absolute beginners</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 rounded bg-zinc-800 flex items-center justify-center text-[#FF5500] shrink-0 mt-0.5">
                  <Check className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-white">Hands-on AI Projects</h4>
                  <p className="text-xs text-zinc-400">Build practical proof to show recruiters</p>
                </div>
              </div>
            </div>

            {/* HERO CTA BUTTONS */}
            <div className="space-y-4 pt-2">
              <button 
                onClick={handleReserveClick}
                data-testid="hero-claim-seat-button"
                className="w-full sm:w-auto bg-[#FF5500] hover:bg-[#E04B00] text-white font-extrabold text-lg py-4 px-8 rounded-lg transition-all duration-300 transform active:scale-95 hover:shadow-xl hover:shadow-[#FF5500]/25 flex items-center justify-center space-x-3"
              >
                <span>Reserve My Free Seat</span>
                <ArrowRight className="w-5 h-5 animate-pulse" />
              </button>
              <p className="text-sm sm:text-base text-zinc-300 font-mono tracking-wide leading-relaxed">
                ⚠️ <span className="text-[#FF5500] font-bold">Limited seats available.</span> Registration will close automatically once the live room capacity is reached.
              </p>
            </div>

          </div>

          {/* Hero Right Column (Speaker Photograph with offset design) */}
          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <div className="relative max-w-[340px] md:max-w-[380px] w-full group">
              {/* Outer stark orange border border */}
              <div className="absolute -inset-3 rounded-2xl border-2 border-[#FF5500] -rotate-3 transition-transform duration-500 group-hover:rotate-0"></div>
              
              {/* Image Container with high depth shadow */}
              <div className="relative bg-zinc-900 p-3 rounded-xl border border-zinc-800 shadow-2xl overflow-hidden">
                <img 
                  src={HERO_SPEAKER_PHOTO} 
                  alt="Namita Desai - AI Mentor & Founder of Future Innovators Club" 
                  data-testid="speaker-namita-desai-image"
                  className="w-full h-auto rounded-lg grayscale group-hover:grayscale-0 transition-all duration-500 object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&q=85";
                  }}
                />
                
                {/* Live session overlay status banner */}
                <div className="absolute top-6 left-6 bg-[#0A0A0A]/90 border border-zinc-800 text-xs font-mono tracking-widest text-zinc-300 py-1 px-3 rounded-full flex items-center space-x-2">
                  <span className="w-2 h-2 rounded-full bg-[#FF5500] animate-pulse"></span>
                  <span>LIVE MASTERCLASS</span>
                </div>

                {/* Speaker Identity Sticker */}
                <div className="absolute bottom-6 left-6 right-6 bg-[#0A0A0A]/95 border border-zinc-800 p-4 rounded-xl backdrop-blur-sm">
                  <div className="font-outfit font-bold text-white text-lg">Namita Desai</div>
                  <div className="text-[#FF5500] text-xs font-mono tracking-wide uppercase mt-0.5">AI Mentor & Career Coach</div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* PAIN POINTS SECTION */}
      <section className="py-16 md:py-24 px-4 md:px-8 max-w-7xl mx-auto">
        <div className="text-center space-y-4 max-w-2xl mx-auto mb-16">
          <span className="text-sm sm:text-base font-extrabold tracking-widest font-mono text-[#FF5500] uppercase">THE STRUGGLE IS REAL</span>
          <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight">
            Still Confused About How to Start Your AI Career?
          </h2>
          <p className="text-zinc-400 text-sm sm:text-base">
            It is not your fault. The industry moves fast, college syllabi are outdated, and tutorial-hell keeps you stuck without real proof of skills.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {painPoints.map((point) => (
            <div 
              key={point.id}
              className="bg-[#121212] border border-zinc-800/80 p-6 rounded-xl relative transition-all duration-300 hover:border-[#FF5500]/30 hover:shadow-xl group"
            >
              <div className="absolute top-4 right-6 text-zinc-700 font-outfit text-5xl font-extrabold select-none group-hover:text-[#FF5500]/10 transition-colors">
                “
              </div>
              <div className="space-y-4">
                <div className="w-8 h-8 rounded bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-400 group-hover:text-[#FF5500] transition-colors">
                  <AlertCircle className="w-4 h-4" />
                </div>
                <h4 className="font-outfit font-semibold text-lg text-white group-hover:text-zinc-100 transition-colors">
                  {point.quote}
                </h4>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  {point.sub}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WEBINAR SOLUTION & MISSION BANNER */}
      <section className="bg-[#121212] border-y border-zinc-900 py-20 px-4 md:px-8">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <span className="text-sm sm:text-base font-extrabold tracking-widest font-mono text-[#FF5500] uppercase">THE CORE TRUTH</span>
          
          <h3 className="font-outfit text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
            “You don’t need to learn everything about AI. <br className="hidden sm:block"/>
            <span className="text-[#FF5500]">You need to learn the right things.</span>”
          </h3>
          
          <p className="text-zinc-300 text-base sm:text-lg max-w-2xl mx-auto font-light leading-relaxed">
            This live, high-impact masterclass is designed to cut down information overload. You don't need to spend years mastering complex theory; you need to understand how to build systems that work today.
          </p>

          <div className="inline-flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <button 
              onClick={handleReserveClick}
              className="bg-[#FF5500] hover:bg-[#E04B00] text-white font-bold py-3 px-8 rounded-lg text-base transition-all duration-300 transform hover:scale-[1.02] flex items-center space-x-2"
            >
              <span>Reserve My Free Seat</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <span className="text-sm font-mono text-zinc-400">
              👉 Come with your questions. Leave with a roadmap.
            </span>
          </div>
        </div>
      </section>

      {/* WEBINAR FRAMEWORK (3 PILLARS WITH INTERACTIVE TABS) */}
      <section className="py-20 px-4 md:px-8 max-w-7xl mx-auto">
        <div className="text-center space-y-4 max-w-2xl mx-auto mb-16">
          <span className="text-sm sm:text-base font-extrabold tracking-widest font-mono text-[#FF5500] uppercase">our proprietary system</span>
          <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            This Masterclass Will Help You Move From Confusion to Clarity
          </h2>
          <p className="text-zinc-400 text-sm sm:text-base">
            We cover the three core pillars essential to establishing an AI career, organized sequentially for continuous skill growth.
          </p>
        </div>

        {/* Dynamic Interactive Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch">
          
          {/* Tab Selection (Left side) */}
          <div className="lg:col-span-5 flex flex-col justify-between space-y-4">
            <div className="space-y-3">
              <button
                onClick={() => setActiveTab("intro")}
                data-testid="agenda-tab-intro"
                className={`w-full text-left p-5 rounded-xl border transition-all duration-300 flex items-start space-x-4 ${
                  activeTab === "intro" 
                  ? "bg-[#121212] border-[#FF5500] shadow-lg shadow-[#FF5500]/5" 
                  : "bg-transparent border-zinc-800 hover:border-zinc-700"
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-outfit font-bold shrink-0 ${
                  activeTab === "intro" ? "bg-[#FF5500] text-black" : "bg-zinc-800 text-zinc-400"
                }`}>
                  1
                </div>
                <div>
                  <h4 className="font-outfit font-bold text-white text-base">Clarity Before Coding</h4>
                  <p className="text-xs text-zinc-400 mt-1">Determine direction & master essential AI foundations</p>
                </div>
              </button>

              <button
                onClick={() => setActiveTab("demo")}
                data-testid="agenda-tab-demo"
                className={`w-full text-left p-5 rounded-xl border transition-all duration-300 flex items-start space-x-4 ${
                  activeTab === "demo" 
                  ? "bg-[#121212] border-[#FF5500] shadow-lg shadow-[#FF5500]/5" 
                  : "bg-transparent border-zinc-800 hover:border-zinc-700"
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-outfit font-bold shrink-0 ${
                  activeTab === "demo" ? "bg-[#FF5500] text-black" : "bg-zinc-800 text-zinc-400"
                }`}>
                  2
                </div>
                <div>
                  <h4 className="font-outfit font-bold text-white text-base">Portfolio Power</h4>
                  <p className="text-xs text-zinc-400 mt-1">Build functional projects and establish public proof</p>
                </div>
              </button>

              <button
                onClick={() => setActiveTab("blueprint")}
                data-testid="agenda-tab-blueprint"
                className={`w-full text-left p-5 rounded-xl border transition-all duration-300 flex items-start space-x-4 ${
                  activeTab === "blueprint" 
                  ? "bg-[#121212] border-[#FF5500] shadow-lg shadow-[#FF5500]/5" 
                  : "bg-transparent border-zinc-800 hover:border-zinc-700"
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-outfit font-bold shrink-0 ${
                  activeTab === "blueprint" ? "bg-[#FF5500] text-black" : "bg-zinc-800 text-zinc-400"
                }`}>
                  3
                </div>
                <div>
                  <h4 className="font-outfit font-bold text-white text-base">The 90-Day Execution System</h4>
                  <p className="text-xs text-zinc-400 mt-1">Implement a structured roadmap with daily progress</p>
                </div>
              </button>

              <button
                onClick={() => setActiveTab("qa")}
                data-testid="agenda-tab-qa"
                className={`w-full text-left p-5 rounded-xl border transition-all duration-300 flex items-start space-x-4 ${
                  activeTab === "qa" 
                  ? "bg-[#121212] border-[#FF5500] shadow-lg shadow-[#FF5500]/5" 
                  : "bg-transparent border-zinc-800 hover:border-zinc-700"
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-outfit font-bold shrink-0 ${
                  activeTab === "qa" ? "bg-[#FF5500] text-black" : "bg-zinc-800 text-zinc-400"
                }`}>
                  4
                </div>
                <div>
                  <h4 className="font-outfit font-bold text-white text-base">Live Q&A With Namita Desai</h4>
                  <p className="text-xs text-zinc-400 mt-1">Direct feedback session & custom roadblock solving</p>
                </div>
              </button>
            </div>
            
            <div className="hidden lg:block bg-zinc-900 border border-zinc-800 p-5 rounded-xl">
              <div className="flex items-center space-x-3 text-[#FF5500]">
                <Clock className="w-5 h-5" />
                <span className="font-mono text-xs uppercase tracking-widest text-zinc-200">session outline</span>
              </div>
              <p className="text-zinc-400 text-sm mt-2">
                This is a fast-paced, high-value 90-minute live session with practical live demos. 100% fluff-free.
              </p>
            </div>
          </div>

          {/* Tab Content Display (Right side) */}
          <div className="lg:col-span-7 bg-[#121212] border border-zinc-800 rounded-xl p-8 flex flex-col justify-between relative overflow-hidden">
            <div className="space-y-6">
              <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
                <span className="text-[#FF5500] font-mono text-xs uppercase tracking-widest font-bold">
                  STEP {activeTab === "intro" ? "01" : activeTab === "demo" ? "02" : activeTab === "blueprint" ? "03" : "04"}
                </span>
              </div>

              <div className="space-y-3">
                <h3 className="font-outfit text-2xl font-extrabold text-white">{agendaTabs[activeTab].title}</h3>
                <p className="text-zinc-300 text-base leading-relaxed">{agendaTabs[activeTab].desc}</p>
              </div>

              <div className="space-y-3.5">
                <div className="text-xs font-mono uppercase text-zinc-400 tracking-wider">Key areas covered:</div>
                <ul className="space-y-3">
                  {agendaTabs[activeTab].bullets.map((bullet, idx) => (
                    <li key={idx} className="flex items-start space-x-3 text-zinc-300 text-sm">
                      <Check className="w-4 h-4 text-[#FF5500] shrink-0 mt-0.5" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="pt-8 border-t border-zinc-800 mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <span className="text-xs font-mono text-zinc-400">Ready to unlock this curriculum?</span>
              <button 
                onClick={handleReserveClick}
                className="inline-flex items-center space-x-2 bg-[#FF5500]/10 border border-[#FF5500]/30 text-[#FF5500] hover:bg-[#FF5500] hover:text-white font-bold py-2.5 px-6 rounded-lg text-sm transition-all duration-300"
              >
                <span>Reserve Free Spot</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

        </div>
      </section>

      {/* WHAT YOU WILL LEARN */}
      <section className="bg-[#121212]/30 border-y border-zinc-900 py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 max-w-2xl mx-auto mb-16">
            <span className="text-sm sm:text-base font-extrabold tracking-widest font-mono text-[#FF5500] uppercase">tangible outcomes</span>
            <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
              What You Will Learn In Just 90 Minutes
            </h2>
            <p className="text-zinc-400 text-sm sm:text-base">
              No generic theory or recycled slides. Learn actual industry practices that make you professional and employable.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {learnings.map((item, index) => (
              <div key={index} className="bg-[#121212] border border-zinc-800 p-6 rounded-xl space-y-4 hover:border-[#FF5500]/20 transition-all duration-300">
                <div className="w-10 h-10 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center font-outfit text-base font-bold text-[#FF5500]">
                  0{index + 1}
                </div>
                <h4 className="font-outfit font-bold text-lg text-white">{item.title}</h4>
                <p className="text-zinc-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO THIS WEBINAR IS FOR */}
      <section className="py-20 px-4 md:px-8 max-w-7xl mx-auto">
        <div className="text-center space-y-4 max-w-2xl mx-auto mb-16">
          <span className="text-sm sm:text-base font-extrabold tracking-widest font-mono text-[#FF5500] uppercase">ideal profiles</span>
          <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            Who This Webinar Is For
          </h2>
          <p className="text-zinc-400 text-sm sm:text-base">
            This masterclass is designed with beginners and freshers in mind. You do not need to be an expert to make a start.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {audienceSegments.map((seg, idx) => (
            <div key={idx} className="bg-zinc-950 border border-zinc-800 p-6 rounded-xl flex items-start space-x-4">
              <div className="w-10 h-10 rounded-full bg-[#FF5500]/10 flex items-center justify-center text-[#FF5500] shrink-0 mt-0.5">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div className="space-y-2">
                <h4 className="font-outfit font-bold text-white text-base">{seg.title}</h4>
                <p className="text-zinc-400 text-sm leading-relaxed">{seg.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT THE SPEAKER SPOTLIGHT */}
      <section className="bg-[#121212] border-y border-zinc-900 py-20 px-4 md:px-8">
        <div className="max-w-6xl mx-auto" data-testid="speaker-bio-card">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Speaker Image Left */}
            <div className="lg:col-span-5 flex justify-center">
              <div className="relative max-w-[320px] w-full">
                <div className="absolute -inset-2 rounded-xl border border-[#FF5500] rotate-2"></div>
                <div className="relative bg-zinc-900 p-2.5 rounded-xl border border-zinc-800">
                  <img 
                    src={SPEAKER_PHOTO} 
                    alt="Namita Desai" 
                    className="w-full h-auto rounded-lg grayscale hover:grayscale-0 transition-all duration-300"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&q=85";
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Speaker Bio Right */}
            <div className="lg:col-span-7 space-y-8">
              <span className="text-sm sm:text-base font-extrabold tracking-widest font-mono text-[#FF5500] uppercase">MEET YOUR MENTOR</span>
              
              <div className="space-y-2">
                <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white">Namita Desai</h2>
                <div className="flex flex-wrap gap-2 items-center text-sm font-mono tracking-wide mt-2">
                  <span className="text-zinc-300 font-bold">AI Mentor & Career Transformation Coach</span>
                  <span className="text-zinc-600 hidden sm:inline">•</span>
                  <span className="text-[#FF5500] font-semibold">Founder, Future Innovators Club</span>
                </div>
                <div className="pt-2">
                  <span className="inline-block bg-[#FF5500]/10 border border-[#FF5500]/30 text-[#FF5500] text-xs font-mono font-bold uppercase tracking-widest py-1 px-3.5 rounded-full">
                    ⚡ 10+ Years in the IT Industry
                  </span>
                </div>
              </div>

              <div className="space-y-4 text-zinc-300 text-sm sm:text-base leading-relaxed font-light font-outfit">
                <p>
                  I’ve experienced the IT journey from multiple sides—as a <strong className="text-white font-semibold">Computer Engineer, technology professional, entrepreneur, and educator</strong>.
                </p>
                <p className="text-zinc-400">
                  Today, my mission is bigger than teaching AI.
                </p>
                <h3 className="text-xl sm:text-2xl font-extrabold text-white leading-tight font-outfit pt-2">
                  I’m building an <span className="text-[#FF5500]">Army of AI Innovators</span> — not just job seekers.
                </h3>
                <p>
                  Through <strong className="text-white font-semibold">Future Innovators Club</strong>, I help people build the skills, confidence, and practical experience they need to become future-ready.
                </p>
              </div>

              {/* Visually Scannable Audience Blocks */}
              <div className="space-y-3 pt-2">
                <div className="text-xs font-mono uppercase text-zinc-400 tracking-wider">FIC is for:</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="bg-zinc-900/50 border border-zinc-850 p-3.5 rounded-lg flex items-center space-x-3 transition-colors hover:border-[#FF5500]/20">
                    <span className="text-xl shrink-0">🎓</span>
                    <div>
                      <span className="block text-xs text-zinc-500 font-mono uppercase tracking-wider">IT Students</span>
                      <span className="text-white font-bold text-sm">Looking to build an AI career</span>
                    </div>
                  </div>
                  <div className="bg-zinc-900/50 border border-zinc-850 p-3.5 rounded-lg flex items-center space-x-3 transition-colors hover:border-[#FF5500]/20">
                    <span className="text-xl shrink-0">💻</span>
                    <div>
                      <span className="block text-xs text-zinc-500 font-mono uppercase tracking-wider">IT Freshers</span>
                      <span className="text-white font-bold text-sm">Preparing for their first job</span>
                    </div>
                  </div>
                  <div className="bg-zinc-900/50 border border-zinc-850 p-3.5 rounded-lg flex items-center space-x-3 transition-colors hover:border-[#FF5500]/20">
                    <span className="text-xl shrink-0">🔄</span>
                    <div>
                      <span className="block text-xs text-zinc-500 font-mono uppercase tracking-wider">Career Break</span>
                      <span className="text-white font-bold text-sm">Restarting their tech career with AI</span>
                    </div>
                  </div>
                  <div className="bg-zinc-900/50 border border-zinc-850 p-3.5 rounded-lg flex items-center space-x-3 transition-colors hover:border-[#FF5500]/20">
                    <span className="text-xl shrink-0">🌱</span>
                    <div>
                      <span className="block text-xs text-zinc-500 font-mono uppercase tracking-wider">Absolute Beginners</span>
                      <span className="text-white font-bold text-sm">Don't know where to start</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Creator Inspiration Block */}
              <div className="space-y-3 pt-2">
                <h4 className="text-sm font-bold uppercase tracking-wider text-zinc-200 font-mono">
                  Why did I create Future Innovators Club?
                </h4>
                <p className="text-zinc-400 text-sm sm:text-base leading-relaxed font-light">
                  Because I believe there is a huge gap between <strong className="text-zinc-200">what we learn in college and what the industry expects</strong>. FIC was created to bridge that gap through clarity, practical learning, real-world projects, mentorship, and community.
                </p>
              </div>

              {/* Custom Quote Box */}
              <div className="relative pt-4">
                <div className="absolute -left-3 top-2 text-[#FF5500]/10 font-outfit text-7xl font-extrabold select-none">
                  “
                </div>
                <blockquote className="border-l-2 border-[#FF5500] pl-5 space-y-2 py-1">
                  <p className="text-zinc-200 text-sm sm:text-base italic font-light leading-relaxed">
                    “You don’t need a fancy background. You just need clarity, the right direction—and someone who truly believes in your growth.”
                  </p>
                  <cite className="block text-[#FF5500] text-xs font-mono font-bold uppercase tracking-wider not-italic font-outfit">
                    — Namita Desai
                  </cite>
                </blockquote>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* FREQUENTLY ASKED QUESTIONS (ACCORDION) */}
      <section className="py-20 px-4 md:px-8 max-w-4xl mx-auto">
        <div className="text-center space-y-4 max-w-2xl mx-auto mb-16">
          <span className="text-sm sm:text-base font-extrabold tracking-widest font-mono text-[#FF5500] uppercase">have questions?</span>
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold tracking-tight text-white">
            Frequently Asked Questions
          </h2>
          <p className="text-zinc-400 text-sm sm:text-base">
            Get clear, simple answers to your topmost concerns about the masterclass.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => {
            const isItemOpen = openFaq === index;
            return (
              <div 
                key={index} 
                data-testid={`faq-item-${index}`}
                className="bg-[#121212] border border-zinc-800 rounded-xl overflow-hidden transition-all duration-300"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-5 text-left font-outfit font-semibold text-white hover:text-[#FF5500] transition-colors"
                >
                  <span className="text-sm sm:text-base">{faq.q}</span>
                  <ChevronDown className={`w-4 h-4 text-zinc-400 shrink-0 ml-4 transition-transform duration-300 ${isItemOpen ? "rotate-180 text-[#FF5500]" : ""}`} />
                </button>
                
                <div className={`transition-all duration-300 ease-in-out ${isItemOpen ? "max-h-[300px] border-t border-zinc-800/50" : "max-h-0"} overflow-hidden`}>
                  <div className="p-5 text-zinc-400 text-xs sm:text-sm leading-relaxed bg-zinc-950/20">
                    {faq.a}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* FINAL HIGH IMPACT CTA SECTION */}
      <section className="py-20 px-4 md:px-8 max-w-5xl mx-auto">
        <div className="bg-[#121212] border border-[#FF5500]/60 rounded-2xl p-8 sm:p-12 text-center relative overflow-hidden space-y-8">
          
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF5500]/5 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#FF5500]/5 rounded-full blur-3xl pointer-events-none"></div>

          <div className="space-y-4 max-w-2xl mx-auto">
            <span className="text-sm sm:text-base font-extrabold tracking-widest font-mono text-[#FF5500] uppercase">TAKE THE FIRST STEP</span>
            <h2 className="font-outfit text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Your AI Career Journey Can Start With One Clear Step.
            </h2>
            <p className="text-zinc-300 text-sm sm:text-base leading-relaxed">
              “You don’t have to know everything before you begin. You just need the right direction.”
            </p>
          </div>

          <div className="space-y-4">
            <button 
              onClick={handleReserveClick}
              data-testid="cta-reserve-button-bottom"
              className="w-full sm:w-auto bg-[#FF5500] hover:bg-[#E04B00] text-white font-extrabold text-lg py-4 px-10 rounded-lg transition-all duration-300 transform active:scale-95 hover:shadow-xl hover:shadow-[#FF5500]/20 inline-flex items-center justify-center space-x-3"
            >
              <span>Reserve My Free Seat</span>
              <ArrowRight className="w-5 h-5" />
            </button>
            <p className="text-xs text-zinc-400 font-mono">
              100% Free Interactive Masterclass • Joining Instructions Provided Instantly
            </p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-zinc-950 border-t border-zinc-900 py-12 px-4 md:px-8 text-center text-zinc-500 text-xs sm:text-sm">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex items-center justify-center">
            <img 
              src={HEADER_LOGO} 
              alt="Future Innovators Club Logo" 
              className="h-16 md:h-20 w-auto object-contain brightness-95"
            />
          </div>

          <p className="max-w-md mx-auto text-zinc-500 text-xs leading-relaxed">
            Designing Future-Ready Innovators. Bridging the skill gap between college-level education and industry-level skills.
          </p>

          <div className="flex items-center justify-center space-x-4 text-xs font-mono text-zinc-400">
            <a href="https://learn.futureinnovatorsclub.com/privacy" className="hover:text-white transition-colors">Privacy Policy</a>
            <span>•</span>
            <a href="https://learn.futureinnovatorsclub.com/terms" className="hover:text-white transition-colors">Terms of Service</a>
          </div>

          <p className="text-zinc-600 text-[10px] font-mono">
            &copy; {new Date().getFullYear()} Future Innovators Club. All rights reserved. Powered by Mango AI Funnel Builder.
          </p>
        </div>
      </footer>

      {/* BOTTOM FLOATING/STICKY MOBILE CTA BAR */}
      <div className={`fixed bottom-0 left-0 right-0 bg-[#0A0A0A]/95 border-t border-zinc-850 py-3 px-4 flex items-center justify-between z-40 transition-transform duration-300 md:hidden ${
        showFloatingCta ? "translate-y-0" : "translate-y-full"
      }`}>
        <div className="text-left">
          <div className="text-white font-bold text-xs">AI Career Blueprint</div>
          <div className="text-[#FF5500] font-mono text-[10px] uppercase font-bold">Upcoming Live Masterclass</div>
        </div>
        <button
          onClick={handleReserveClick}
          data-testid="floating-cta-button"
          className="bg-[#FF5500] hover:bg-[#E04B00] text-white font-bold text-xs py-2.5 px-4 rounded-lg flex items-center space-x-1.5 transition-all duration-300"
        >
          <span>Claim Seat</span>
          <ArrowRight className="w-3 h-3" />
        </button>
      </div>


      {/* LEAD CAPTURE POPUP / MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 z-50 overflow-y-auto animate-fade-in">
          <div className="bg-[#121212] border border-zinc-800 rounded-2xl max-w-lg w-full p-6 relative max-h-[90vh] overflow-y-auto space-y-6">
            
            {/* Close Button */}
            <button 
              onClick={() => setIsModalOpen(false)}
              className="w-8 h-8 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white absolute right-4 top-4 transition-all duration-300"
              aria-label="Close form"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Header */}
            <div className="text-center space-y-1.5">
              <h3 className="font-outfit text-2xl font-extrabold text-white">Reserve Your FREE Spot</h3>
              <p className="text-zinc-400 text-sm">
                Enter your details below to continue to registration.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleFormSubmit} className="space-y-4" data-testid="reservation-form">
              
              <div className="text-xs font-mono uppercase tracking-widest text-[#FF5500] border-b border-zinc-800/80 pb-1 font-bold">
                Contact Details
              </div>

              {/* Full Name */}
              <div className="space-y-1.5">
                <label className="text-xs font-mono uppercase text-zinc-400 tracking-wider flex justify-between">
                  <span>Full Name</span>
                  <span className="text-red-500 font-bold">*</span>
                </label>
                <input 
                  type="text" 
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  data-testid="reservation-input-name"
                  placeholder="Enter student's or parent's name"
                  className={`w-full bg-zinc-900/80 border ${errors.fullName ? "border-red-500 focus:border-red-500" : "border-zinc-800 focus:border-[#FF5500]"} rounded-lg px-4 py-3 text-sm text-white placeholder-zinc-500 focus:outline-none transition-colors`}
                />
                {errors.fullName && <p className="text-xs text-red-500">{errors.fullName}</p>}
              </div>

              {/* Email Address */}
              <div className="space-y-1.5">
                <label className="text-xs font-mono uppercase text-zinc-400 tracking-wider flex justify-between">
                  <span>Email Address</span>
                  <span className="text-red-500 font-bold">*</span>
                </label>
                <input 
                  type="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  data-testid="reservation-input-email"
                  placeholder="name@domain.com"
                  className={`w-full bg-zinc-900/80 border ${errors.email ? "border-red-500 focus:border-red-500" : "border-zinc-800 focus:border-[#FF5500]"} rounded-lg px-4 py-3 text-sm text-white placeholder-zinc-500 focus:outline-none transition-colors`}
                />
                {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
              </div>

              {/* WhatsApp Number */}
              <div className="space-y-1.5">
                <label className="text-xs font-mono uppercase text-zinc-400 tracking-wider flex justify-between">
                  <span>WhatsApp Number</span>
                  <span className="text-red-500 font-bold">*</span>
                </label>
                <input 
                  type="tel" 
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  data-testid="reservation-input-phone"
                  placeholder="Mobile / WhatsApp with country code"
                  className={`w-full bg-zinc-900/80 border ${errors.phone ? "border-red-500 focus:border-red-500" : "border-zinc-800 focus:border-[#FF5500]"} rounded-lg px-4 py-3 text-sm text-white placeholder-zinc-500 focus:outline-none transition-colors`}
                />
                {errors.phone && <p className="text-xs text-red-500">{errors.phone}</p>}
              </div>

              <div className="text-xs font-mono uppercase tracking-widest text-[#FF5500] border-b border-zinc-800/80 pb-1 pt-2 font-bold">
                Qualification Questions
              </div>

              {/* Q1: Situation Dropdown */}
              <div className="space-y-1.5">
                <label className="text-xs font-mono uppercase text-zinc-400 tracking-wider flex justify-between">
                  <span>Q1. What best describes you?</span>
                  <span className="text-red-500 font-bold">*</span>
                </label>
                <div className="relative">
                  <select
                    name="situation"
                    value={formData.situation}
                    onChange={handleInputChange}
                    data-testid="reservation-select-age"
                    className={`w-full bg-zinc-900/80 border ${errors.situation ? "border-red-500" : "border-zinc-800"} rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-[#FF5500] transition-colors appearance-none cursor-pointer pr-10`}
                  >
                    <option value="">-- Select Option --</option>
                    <option value="IT Student">IT Student</option>
                    <option value="IT Fresher / Looking for Job">IT Fresher / Looking for Job</option>
                    <option value="Working IT Professional">Working IT Professional</option>
                    <option value="On a Career Break and want to Restart using AI">On a Career Break and want to Restart using AI</option>
                    <option value="AI Beginner">AI Beginner</option>
                    <option value="Other">Other</option>
                  </select>
                  <ChevronDown className="w-4 h-4 text-zinc-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
                {errors.situation && <p className="text-xs text-red-500">{errors.situation}</p>}
              </div>

              {/* Q2: Education Dropdown */}
              <div className="space-y-1.5">
                <label className="text-xs font-mono uppercase text-zinc-400 tracking-wider flex justify-between">
                  <span>Q2. What is your educational background?</span>
                  <span className="text-red-500 font-bold">*</span>
                </label>
                <div className="relative">
                  <select
                    name="education"
                    value={formData.education}
                    onChange={handleInputChange}
                    className={`w-full bg-zinc-900/80 border ${errors.education ? "border-red-500" : "border-zinc-800"} rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-[#FF5500] transition-colors appearance-none cursor-pointer pr-10`}
                  >
                    <option value="">-- Select Option --</option>
                    <option value="Computer Science / IT">Computer Science / IT</option>
                    <option value="BCA / MCA">BCA / MCA</option>
                    <option value="B.Sc. / M.Sc.">B.Sc. / M.Sc.</option>
                    <option value="Diploma">Diploma</option>
                    <option value="Other Bachelor's Degree">Other Bachelor's Degree</option>
                    <option value="Master's Degree">Master's Degree</option>
                    <option value="Currently pursuing my degree">Currently pursuing my degree</option>
                    <option value="Other">Other</option>
                  </select>
                  <ChevronDown className="w-4 h-4 text-zinc-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
                {errors.education && <p className="text-xs text-red-500">{errors.education}</p>}
              </div>

              {/* Q3: Challenge Dropdown */}
              <div className="space-y-1.5">
                <label className="text-xs font-mono uppercase text-zinc-400 tracking-wider flex justify-between">
                  <span>Q3. What is your biggest challenge right now?</span>
                  <span className="text-red-500 font-bold">*</span>
                </label>
                <div className="relative">
                  <select
                    name="challenge"
                    value={formData.challenge}
                    onChange={handleInputChange}
                    className={`w-full bg-zinc-900/80 border ${errors.challenge ? "border-red-500" : "border-zinc-800"} rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-[#FF5500] transition-colors appearance-none cursor-pointer pr-10`}
                  >
                    <option value="">-- Select Option --</option>
                    <option value="I don't know what AI skills I should learn">I don't know what AI skills I should learn</option>
                    <option value="I lack practical/project experience">I lack practical/project experience</option>
                    <option value="I know AI but don't know what projects to build">I know AI but don't know what projects to build</option>
                    <option value="My programming skills are weak">My programming skills are weak</option>
                    <option value="I find AI concepts difficult">I find AI concepts difficult</option>
                    <option value="I struggle with consistency">I struggle with consistency</option>
                    <option value="I am struggling to become job-ready">I am struggling to become job-ready</option>
                    <option value="I don't know which AI career path to choose">I don't know which AI career path to choose</option>
                    <option value="I am not getting interviews/jobs">I am not getting interviews/jobs</option>
                    <option value="I don't have a strong portfolio/resume">I don't have a strong portfolio/resume</option>
                    <option value="I'm not confident about interviews">I'm not confident about interviews</option>
                    <option value="I am on a career break and don't know how to restart">I am on a career break and don't know how to restart</option>
                    <option value="I feel overwhelmed by the rapidly changing AI tools">I feel overwhelmed by the rapidly changing AI tools</option>
                    <option value="I want practical guidance,Mentorship and a clear roadmap">I want practical guidance,Mentorship and a clear roadmap</option>
                    <option value="Other">Other</option>
                  </select>
                  <ChevronDown className="w-4 h-4 text-zinc-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
                {errors.challenge && <p className="text-xs text-red-500">{errors.challenge}</p>}
              </div>

              {/* Submit Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="reservation-submit-button"
                  className="w-full bg-[#FF5500] hover:bg-[#E04B00] disabled:bg-zinc-800 disabled:text-zinc-600 text-white font-extrabold py-3.5 px-6 rounded-lg text-sm transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg shadow-[#FF5500]/15"
                >
                  {isSubmitting ? (
                    <>
                      <span className="w-4 h-4 rounded-full border-2 border-white/20 border-t-white animate-spin"></span>
                      <span>Saving your details...</span>
                    </>
                  ) : (
                    <>
                      <span>Continue to Registration</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>

              <p className="text-[10px] text-zinc-500 text-center leading-relaxed font-mono">
                🔒 Your details are secure and passed to your official registration checkout page.
              </p>
            </form>
          </div>
        </div>
      )}


      {/* SUCCESS CONFIRMATION & WHATSAPP JOIN POPUP (FALLBACK CALLBACK IF ACCESSED VIA REDIRECT ARGS) */}
      {isWhatsappPopupOpen && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 z-50 animate-fade-in" data-testid="reservation-success-view">
          <div className="bg-[#121212] border-2 border-[#FF5500] rounded-2xl max-w-md w-full p-6 text-center space-y-6 relative">
            <button 
              onClick={() => setIsWhatsappPopupOpen(false)}
              className="w-8 h-8 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white absolute right-4 top-4 transition-all duration-300"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="w-16 h-16 rounded-full bg-[#FF5500]/10 border border-[#FF5500]/30 flex items-center justify-center mx-auto text-[#FF5500]">
              <MessageSquare className="w-8 h-8 animate-bounce" />
            </div>

            <div className="space-y-2">
              <h3 className="font-outfit text-2xl font-extrabold text-white">Registration Confirmed! 🎉</h3>
              <p className="text-zinc-300 text-sm">
                Awesome! Your free seat has been officially secured. Join our VIP WhatsApp community below for direct alerts and class blueprints.
              </p>
              <p className="text-zinc-500 text-xs font-mono animate-pulse">
                Auto-redirecting to WhatsApp Group in 3 seconds...
              </p>
            </div>

            <div className="space-y-3">
              <a 
                href={WHATSAPP_COMMUNITY_LINK}
                className="w-full bg-[#FF5500] hover:bg-[#E04B00] text-white font-extrabold py-3.5 px-6 rounded-xl transition-all duration-300 flex items-center justify-center space-x-2 text-base shadow-xl shadow-[#FF5500]/25"
              >
                <span>🚀 Join VIP Community & Get Live Updates</span>
                <ArrowRight className="w-5 h-5" />
              </a>
              
              <button
                onClick={() => setIsWhatsappPopupOpen(false)}
                className="text-zinc-500 hover:text-zinc-300 text-xs font-mono transition-colors"
              >
                I'll join later
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}